/**
 * =====================================================
 * TAKEOUT ORDER APPLICATION
 * 3-Step Flow: Contact Info → Menu → Checkout
 * =====================================================
 * Step 1: Contact Details
 * Step 2: Menu Selection
 * Step 3: Summary & Payment
 */

const TakeoutApp = (function() {
  'use strict';

  // ============== STATE ==============
  let currentStep = 1;
  let menuData = null;
  let cart = [];
  let activeCategory = 'all';
  let activeFilter = 'all';
  let searchQuery = '';
  let stripe = null;
  let cardElement = null;
  let walletElements = null; // Apple Pay / Google Pay buttons
  let walletAmount = 0;      // total shown in the Apple Pay / Google Pay sheet

  // Order created on the server for the current checkout attempt.
  // Re-used on retries so a failed attempt never creates a second
  // order or a second charge: { key, orderId, totals, paid }
  let pendingOrder = null;

  // ============== CONFIG ==============
  const config = window.TakeoutConfig || {};

  // ============== INITIALIZATION ==============

  function init() {
    console.log('TakeoutApp: Initializing...');

    setupEventListeners();

    // Initialize Stripe if needed
    if (config.showPayment && config.stripe?.publishableKey) {
      initStripe();
    }

    // Check availability before showing the form
    checkAvailability();

    console.log('TakeoutApp: Ready');
  }

  /**
   * Ask the backend whether online ordering is open right now.
   * The backend decides using restaurant time: closed days,
   * paused dates and ordering hours. If it says no, show the
   * closed state.
   */
  async function checkAvailability() {
    const loadingEl = document.getElementById('takeout-availability-check');
    const mainContent = document.getElementById('takeout-main-content');

    if (config.orderApiUrl) {
      try {
        const response = await fetch(`${config.orderApiUrl}?action=checkTakeoutStatus`);
        const data = await response.json();

        // Show the ordering hours under the page title (closed-day and
        // outside-hours messages already include them)
        const hoursEl = document.getElementById('takeout-hours');
        if (hoursEl && data.hours && (!data.paused || data.reason === 'paused')) {
          hoursEl.textContent = `Online ordering: ${data.hours}`;
          hoursEl.hidden = false;
        }

        if (data.success && data.paused) {
          showClosedState(
            data.title || 'Ordering Paused',
            data.message || config.pausedMessage || 'Online ordering is temporarily paused for today.'
          );
          return;
        }
      } catch (err) {
        // If the API call fails, let the user proceed — don't block ordering
        // just because the status check had a network hiccup
        console.warn('TakeoutApp: Could not check pause status, proceeding anyway.', err);
      }
    }

    // All clear — show the form
    if (loadingEl) loadingEl.style.display = 'none';
    if (mainContent) mainContent.style.display = 'block';
  }

  /**
   * Show the closed / paused overlay and hide the form
   */
  function showClosedState(title, message) {
    const loadingEl = document.getElementById('takeout-availability-check');
    const mainContent = document.getElementById('takeout-main-content');
    const closedEl = document.getElementById('takeout-closed');

    if (loadingEl) loadingEl.style.display = 'none';
    if (mainContent) mainContent.style.display = 'none';
    if (closedEl) {
      document.getElementById('closed-title').textContent = title;
      document.getElementById('closed-message').textContent = message;
      closedEl.style.display = 'block';
    }
  }

  function setupEventListeners() {
    // Menu search
    const searchInput = document.getElementById('menu-search');
    if (searchInput) {
      searchInput.addEventListener('input', debounce(handleSearch, 300));
    }

    // Search clear
    const searchClear = document.getElementById('search-clear');
    if (searchClear) {
      searchClear.addEventListener('click', clearSearch);
    }

    // Dietary filters
    document.querySelectorAll('.filter-tag').forEach(tag => {
      tag.addEventListener('click', () => handleFilterClick(tag));
    });

    // Escape closes the mobile order sheet
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMobileCart();
    });
  }


  // ============== STEP NAVIGATION ==============

  function goToStep(step) {
    // Validate before moving forward
    if (step > currentStep) {
      if (!validateCurrentStep()) {
        return;
      }
    }

    // Load menu when entering step 2 for the first time
    if (step === 2 && !menuData) {
      loadMenu();
    }

    // Populate summary when entering step 3
    if (step === 3) {
      populateSummary();
    }

    // The mobile cart drawer lives in step 2 — close it (and its page
    // overlay) so it can't cover the next step
    closeMobileCart();

    // Hide all steps
    document.querySelectorAll('.form-step').forEach(el => {
      el.style.display = 'none';
    });

    // Show target step
    const targetStep = document.querySelector(`[data-form-step="${step}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
    }

    // Update step indicators
    document.querySelectorAll('.step').forEach(el => {
      const stepNum = parseInt(el.dataset.step);
      el.classList.remove('active', 'completed');

      if (stepNum < step) {
        el.classList.add('completed');
      } else if (stepNum === step) {
        el.classList.add('active');
      }
    });

    // Update step lines
    document.querySelectorAll('.step-line').forEach(el => {
      const lineNum = parseInt(el.dataset.line);
      el.classList.toggle('completed', lineNum < step);
    });

    currentStep = step;
    hideMessage();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function validateCurrentStep() {
    if (currentStep === 1) {
      return validateStep1();
    } else if (currentStep === 2) {
      return validateStep2();
    }
    return true;
  }

  function validateStep1() {
    const name = document.getElementById('takeout-name')?.value.trim();
    const email = document.getElementById('takeout-email')?.value.trim();
    const phone = document.getElementById('takeout-phone')?.value.trim();

    if (!name || !email || !phone) {
      showMessage(config.messages?.fillRequired || 'Please fill in all required fields', 'error');
      return false;
    }

    if (!isValidEmail(email)) {
      showMessage(config.messages?.invalidEmail || 'Please enter a valid email address', 'error');
      return false;
    }

    return true;
  }

  function validateStep2() {
    if (cart.length === 0) {
      showMessage(config.messages?.emptyCart || 'Please add at least one item to your order', 'error');
      return false;
    }
    return true;
  }


  // ============== STEP 2: MENU SELECTION ==============

  async function loadMenu() {
    const loading = document.getElementById('menu-loading');
    const errorEl = document.getElementById('menu-error');
    const grid = document.getElementById('menu-items-grid');

    loading.style.display = 'flex';
    errorEl.style.display = 'none';
    grid.style.display = 'none';

    try {
      // For takeout, no date parameter needed — load full menu
      const response = await fetch(`${config.menuApiUrl}?action=getMenu`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load menu');
      }

      menuData = data.data;
      console.log('Menu loaded:', menuData);

      renderCategories();
      renderMenuItems();

      loading.style.display = 'none';
      grid.style.display = 'block';

    } catch (error) {
      console.error('Error loading menu:', error);
      loading.style.display = 'none';
      errorEl.style.display = 'flex';
      document.getElementById('menu-error-text').textContent = error.message;
    }
  }

  function renderCategories() {
    const container = document.getElementById('category-tabs');
    if (!container || !menuData?.categories) return;

    const inner = container.querySelector('.category-tabs-inner');
    inner.innerHTML = '';

    // All tab
    const allTab = createCategoryTab({ category: 'all', display_name: 'All', icon: '🍽️' });
    allTab.classList.add('active');
    inner.appendChild(allTab);

    // Category tabs
    menuData.categories.forEach(cat => {
      inner.appendChild(createCategoryTab(cat));
    });
  }

  function createCategoryTab(cat) {
    const template = document.getElementById('category-tab-template');
    const clone = template.content.cloneNode(true);
    const btn = clone.querySelector('.category-tab');

    btn.dataset.category = cat.category;
    btn.querySelector('.category-icon').textContent = cat.icon || '🍴';
    btn.querySelector('.category-name').textContent = cat.display_name || cat.category;

    btn.addEventListener('click', () => {
      document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
      btn.classList.add('active');
      activeCategory = cat.category;
      renderMenuItems();

      // If the tabs are pinned while scrolling, jump back to the start of the menu
      const menuSection = document.querySelector('.menu-section');
      if (menuSection && menuSection.getBoundingClientRect().top < 0) {
        menuSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    return btn;
  }

  /**
   * Render the menu grouped by category (in the Categories sheet order),
   * each group under its own heading — like the printed menu.
   */
  function renderMenuItems() {
    const grid = document.getElementById('menu-items-grid');
    const noResults = document.getElementById('no-results');

    if (!grid || !menuData?.allItems) return;

    grid.innerHTML = '';

    let items = getFilteredItems();

    if (items.length === 0) {
      grid.style.display = 'none';
      noResults.style.display = 'flex';
      return;
    }

    grid.style.display = 'block';
    noResults.style.display = 'none';

    const categories = menuData.categories || [];
    const categoryOrder = categories.map(c => c.category);
    const rank = name => {
      const i = categoryOrder.indexOf(name);
      return i === -1 ? categoryOrder.length : i;
    };
    const displayName = name => {
      const cat = categories.find(c => c.category === name);
      return String((cat && cat.display_name) || name || 'More').trim();
    };

    const groups = new Map();
    items.forEach(item => {
      if (!groups.has(item.category)) groups.set(item.category, []);
      groups.get(item.category).push(item);
    });

    [...groups.keys()].sort((a, b) => rank(a) - rank(b)).forEach(category => {
      const group = document.createElement('section');
      group.className = 'menu-group';

      const heading = document.createElement('h3');
      heading.className = 'menu-group-title';
      heading.textContent = displayName(category);
      group.appendChild(heading);

      // MLCC: customers ordering alcohol must show picture ID at pickup
      if (groups.get(category).some(item => item.alcohol === true)) {
        const notice = document.createElement('p');
        notice.className = 'menu-group-notice';
        notice.textContent = config.messages?.alcoholIdNotice ||
          'As per MLCC guidelines, picture ID must be shown at pickup for age verification. If ID can\'t be verified, drinks are refunded minus a 4% administration fee.';
        group.appendChild(notice);
      }

      groups.get(category).forEach(item => group.appendChild(createMenuItemCard(item)));
      grid.appendChild(group);
    });

    updateCartIndicators();
  }

  function getFilteredItems() {
    if (!menuData?.allItems) return [];

    let items = [...menuData.allItems];

    // Category filter
    if (activeCategory !== 'all') {
      items = items.filter(i => i.category === activeCategory);
    }

    // Dietary filter
    if (activeFilter !== 'all') {
      items = items.filter(i => i.dietary_tags?.includes(activeFilter));
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.name.toLowerCase().includes(q) ||
        (i.description && i.description.toLowerCase().includes(q))
      );
    }

    return items;
  }

  function createMenuItemCard(item) {
    const template = document.getElementById('menu-item-template');
    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.menu-item-card');

    card.dataset.itemId = item.id;
    card.dataset.category = item.category;
    card.dataset.tags = (item.dietary_tags || []).join(',');

    // Content
    card.querySelector('.menu-item-name').textContent = item.name;
    card.querySelector('.menu-item-price').textContent = formatCurrency(item.price);
    card.querySelector('.menu-item-description').textContent = item.description || '';

    // Tags
    const tagsEl = card.querySelector('.menu-item-tags');
    if (item.dietary_tags?.length) {
      tagsEl.innerHTML = item.dietary_tags.map(tag => {
        const slug = String(tag).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return `<span class="tag tag-${escapeHtml(slug)}">${escapeHtml(formatTag(tag))}</span>`;
      }).join('');
    }

    // Spice selector — only the levels listed for this item on the menu sheet
    const spiceEl = card.querySelector('.menu-item-spice');
    const spiceOptions = getSpiceOptions(item);
    if (spiceOptions.length) {
      spiceEl.innerHTML = `
        <div class="menu-spice-selector" role="radiogroup" aria-label="Spice level for ${escapeHtml(item.name)}">
          <span class="menu-spice-label" aria-hidden="true">Spice</span>
          <div class="menu-spice-options">
            ${spiceOptions.map((level, i) => `
              <label class="menu-spice-option">
                <input type="radio" name="spice-${escapeHtml(item.id)}" value="${escapeHtml(level)}"${i === 0 ? ' checked' : ''}>
                <span class="menu-spice-badge">${escapeHtml(formatSpice(level))}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
      card.dataset.hasSpice = 'true';
    }

    // Sides
    const sidesEl = card.querySelector('.menu-item-sides');
    if (item.sides && item.sides.length > 0) {
      sidesEl.innerHTML = `
        <div class="menu-sides-selector" role="radiogroup" aria-label="Side for ${escapeHtml(item.name)}">
          <span class="menu-sides-label" aria-hidden="true">Side</span>
          <div class="menu-sides-options">
            ${item.sides.map((side, i) => `
              <label class="menu-side-option">
                <input type="radio" name="side-${escapeHtml(item.id)}" value="${escapeHtml(side)}"${i === 0 ? ' checked' : ''}>
                <span class="menu-side-badge">${escapeHtml(side)}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
      card.dataset.hasSides = 'true';
    }

    return card;
  }

  function handleFilterClick(btn) {
    const isActive = btn.classList.contains('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));

    if (!isActive) {
      btn.classList.add('active');
      activeFilter = btn.dataset.filter;
    } else {
      document.querySelector('.filter-tag[data-filter="all"]')?.classList.add('active');
      activeFilter = 'all';
    }

    renderMenuItems();
  }

  function handleSearch(e) {
    searchQuery = e.target.value.trim();
    document.getElementById('search-clear').style.display = searchQuery ? 'block' : 'none';
    renderMenuItems();
  }

  function clearSearch() {
    document.getElementById('menu-search').value = '';
    searchQuery = '';
    document.getElementById('search-clear').style.display = 'none';
    renderMenuItems();
  }

  function clearFilters() {
    activeCategory = 'all';
    activeFilter = 'all';
    searchQuery = '';

    document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
    document.querySelector('.category-tab[data-category="all"]')?.classList.add('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));
    document.querySelector('.filter-tag[data-filter="all"]')?.classList.add('active');

    document.getElementById('menu-search').value = '';
    document.getElementById('search-clear').style.display = 'none';

    renderMenuItems();
  }


  // ============== CART MANAGEMENT ==============

  function addToCart(btn) {
    const card = btn.closest('.menu-item-card');
    const itemId = card.dataset.itemId;
    const item = menuData.allItems.find(i => i.id === itemId);

    if (!item) return;

    // Get selected spice level (defaults to the first listed level)
    const spiceOptions = getSpiceOptions(item);
    let selectedSpice = null;
    if (spiceOptions.length) {
      const checkedSpice = card.querySelector('.menu-item-spice input[type="radio"]:checked');
      selectedSpice = checkedSpice && spiceOptions.includes(checkedSpice.value) ? checkedSpice.value : spiceOptions[0];
    }

    // Get selected side
    let selectedSide = null;
    if (item.sides && item.sides.length > 0) {
      const checkedRadio = card.querySelector(`input[name="side-${item.id}"]:checked`);
      selectedSide = checkedRadio ? checkedRadio.value : item.sides[0];
    }

    const existing = cart.find(c => c.id === itemId);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        spiceLevel: selectedSpice,
        selectedSide: selectedSide
      });
    }

    updateCartUI();
    updateCartIndicators();
  }

  function updateCartItem(btn, delta) {
    const card = btn.closest('.menu-item-card');
    const itemId = card.dataset.itemId;

    const item = cart.find(c => c.id === itemId);
    if (!item) return;

    item.quantity += delta;

    if (item.quantity <= 0) {
      cart = cart.filter(c => c.id !== itemId);
    }

    updateCartUI();
    updateCartIndicators();
  }

  function updateCartItemFromCart(btn, delta) {
    const cartItem = btn.closest('.cart-item');
    const itemId = cartItem.dataset.itemId;

    const item = cart.find(c => c.id === itemId);
    if (!item) return;

    item.quantity += delta;

    if (item.quantity <= 0) {
      cart = cart.filter(c => c.id !== itemId);
    }

    updateCartUI();
    updateCartIndicators();
  }

  function removeFromCart(btn) {
    const cartItem = btn.closest('.cart-item');
    const itemId = cartItem.dataset.itemId;

    cart = cart.filter(c => c.id !== itemId);

    updateCartUI();
    updateCartIndicators();
  }

  function clearCart() {
    if (!confirm('Clear all items from your cart?')) return;
    cart = [];
    updateCartUI();
    updateCartIndicators();
  }

  function getCartTotals() {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    const taxPercent = parseFloat(config.taxPercent) || 0;
    const gratuityPercent = config.showGratuity ? (parseFloat(config.gratuityPercent) || 0) : 0;

    const tax = Math.round(subtotal * (taxPercent / 100));
    const gratuity = Math.round(subtotal * (gratuityPercent / 100));
    const total = subtotal + tax + gratuity;

    return { subtotal, tax, gratuity, total, itemCount };
  }

  function updateCartUI() {
    const totals = getCartTotals();
    const isEmpty = cart.length === 0;

    // Counts
    document.getElementById('cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-total').textContent = formatCurrency(totals.total);
    document.getElementById('mobile-cart-toggle')?.classList.toggle('is-empty', isEmpty);

    // Toggle visibility
    document.getElementById('cart-empty').style.display = isEmpty ? 'block' : 'none';
    document.getElementById('cart-items').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('cart-summary').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('clear-cart-btn').style.display = isEmpty ? 'none' : 'block';

    // Show/hide cart continue button
    const cartContinueBtn = document.getElementById('cart-continue-btn');
    if (cartContinueBtn) {
      cartContinueBtn.style.display = isEmpty ? 'none' : 'block';
    }

    // Update totals
    document.getElementById('cart-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('cart-tax').textContent = formatCurrency(totals.tax);

    const gratuityEl = document.getElementById('cart-gratuity');
    if (gratuityEl) {
      gratuityEl.textContent = formatCurrency(totals.gratuity);
    }

    document.getElementById('cart-total').textContent = formatCurrency(totals.total);

    // Render cart items
    renderCartItems();

    // Enable/disable continue button
    const continueBtn = document.getElementById('continue-to-checkout-btn');
    if (continueBtn) {
      continueBtn.disabled = isEmpty;
    }
  }

  function renderCartItems() {
    const container = document.getElementById('cart-items');
    container.innerHTML = '';

    cart.forEach(item => {
      const template = document.getElementById('cart-item-template');
      const clone = template.content.cloneNode(true);
      const cartItem = clone.querySelector('.cart-item');

      cartItem.dataset.itemId = item.id;
      cartItem.querySelector('.cart-item-name').textContent = item.name;
      cartItem.querySelector('.cart-item-price').textContent = formatCurrency(item.price * item.quantity);
      cartItem.querySelector('.cart-item-qty').textContent = item.quantity;

      // Look up full menu item
      const menuItem = menuData?.allItems?.find(m => m.id === item.id);

      // Spice selector — its own radio group ("cart-spice-…") so it
      // doesn't fight with the same item's selector on the menu card
      const spiceContainer = cartItem.querySelector('.cart-item-spice');
      const spiceOptions = menuItem ? getSpiceOptions(menuItem) : [];
      if (spiceOptions.length) {
        if (!spiceOptions.includes(item.spiceLevel)) item.spiceLevel = spiceOptions[0];
        spiceContainer.querySelector('.cart-spice-options').innerHTML = spiceOptions.map(level => `
          <label class="cart-spice-option">
            <input type="radio" name="cart-spice-${escapeHtml(item.id)}" value="${escapeHtml(level)}"${item.spiceLevel === level ? ' checked' : ''}>
            <span class="cart-spice-badge">${escapeHtml(formatSpice(level))}</span>
          </label>
        `).join('');
        spiceContainer.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.addEventListener('change', () => {
            const cartEntry = cart.find(c => c.id === item.id);
            if (cartEntry) cartEntry.spiceLevel = radio.value;
          });
        });
        spiceContainer.style.display = ''; // layout comes from the stylesheet
      } else {
        spiceContainer.style.display = 'none';
      }

      // Side selector
      const sideContainer = cartItem.querySelector('.cart-item-sides');
      if (menuItem && menuItem.sides && menuItem.sides.length > 0) {
        if (!item.selectedSide) item.selectedSide = menuItem.sides[0];
        sideContainer.innerHTML = `
          <span class="cart-sides-label">Side</span>
          <div class="cart-sides-options">
            ${menuItem.sides.map(side => `
              <label class="cart-side-option">
                <input type="radio" name="cart-side-${escapeHtml(item.id)}" value="${escapeHtml(side)}"${item.selectedSide === side ? ' checked' : ''}>
                <span class="cart-side-badge">${escapeHtml(side)}</span>
              </label>
            `).join('')}
          </div>
        `;
        sideContainer.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.addEventListener('change', () => {
            const cartEntry = cart.find(c => c.id === item.id);
            if (cartEntry) cartEntry.selectedSide = radio.value;
          });
        });
        sideContainer.style.display = ''; // layout comes from the stylesheet
      } else {
        sideContainer.style.display = 'none';
      }

      container.appendChild(cartItem);
    });
  }

  function updateCartIndicators() {
    document.querySelectorAll('.menu-item-card').forEach(card => {
      const itemId = card.dataset.itemId;
      const cartItem = cart.find(c => c.id === itemId);

      const actions = card.querySelector('.menu-item-actions');
      const inCart = card.querySelector('.menu-item-in-cart');

      if (cartItem) {
        actions.style.display = 'none';
        inCart.style.display = 'flex';
        inCart.querySelector('.in-cart-qty').textContent = cartItem.quantity;
        card.classList.add('in-cart');
      } else {
        actions.style.display = 'flex';
        inCart.style.display = 'none';
        card.classList.remove('in-cart');
      }
    });
  }

  function toggleMobileCart() {
    const sidebar = document.getElementById('cart-sidebar');
    const open = sidebar.classList.toggle('mobile-open');
    document.body.classList.toggle('takeout-cart-open', open);
    if (open) sidebar.querySelector('.cart-close-btn')?.focus();
  }

  function closeMobileCart() {
    const sidebar = document.getElementById('cart-sidebar');
    const wasOpen = sidebar?.classList.contains('mobile-open');
    sidebar?.classList.remove('mobile-open');
    document.body.classList.remove('takeout-cart-open');
    if (wasOpen) document.getElementById('mobile-cart-toggle')?.focus({ preventScroll: true });
  }


  // ============== STEP 3: SUMMARY & PAYMENT ==============

  function populateSummary() {
    // Contact info
    document.getElementById('summary-name').textContent = document.getElementById('takeout-name')?.value || '';
    document.getElementById('summary-email').textContent = document.getElementById('takeout-email')?.value || '';
    document.getElementById('summary-phone').textContent = document.getElementById('takeout-phone')?.value || '';

    // Order items
    const orderContainer = document.getElementById('summary-order-items');
    orderContainer.innerHTML = '';

    cart.forEach(item => {
      const div = document.createElement('div');
      div.className = 'summary-order-item';
      const spiceLabel = item.spiceLevel ? ` <span class="summary-spice-badge">${escapeHtml(item.spiceLevel.charAt(0).toUpperCase() + item.spiceLevel.slice(1))}</span>` : '';
      const sideLabel = item.selectedSide ? ` <span class="summary-side-badge">+ ${escapeHtml(item.selectedSide)}</span>` : '';
      div.innerHTML = `
        <span class="summary-item-qty">${item.quantity}×</span>
        <span class="summary-item-name">${escapeHtml(item.name)}${spiceLabel}${sideLabel}</span>
        <span class="summary-item-price">${formatCurrency(item.price * item.quantity)}</span>
      `;
      orderContainer.appendChild(div);
    });

    // Totals
    const totals = getCartTotals();
    document.getElementById('summary-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('summary-tax').textContent = formatCurrency(totals.tax);

    const gratuityEl = document.getElementById('summary-gratuity');
    if (gratuityEl) {
      gratuityEl.textContent = formatCurrency(totals.gratuity);
    }

    document.getElementById('summary-total').textContent = formatCurrency(totals.total);

    updateWalletButtons(totals.total);
  }

  function initStripe() {
    if (!config.stripe?.publishableKey) return;

    try {
      stripe = Stripe(config.stripe.publishableKey);
      // Load the site's body font (Asap) inside Stripe's secure card field
      const elements = stripe.elements({
        fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Asap:wght@400;600&display=swap' }]
      });

      cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#2c2c2c',
            iconColor: '#3a7d44',
            fontFamily: 'Asap, "Segoe UI", system-ui, sans-serif',
            '::placeholder': { color: '#767676' }
          },
          invalid: {
            color: '#c00a27',
            iconColor: '#c00a27'
          }
        }
      });

      cardElement.mount('#card-element');

      cardElement.on('change', (event) => {
        const errorEl = document.getElementById('card-errors');
        errorEl.textContent = event.error ? event.error.message : '';
      });

      console.log('Stripe initialized');
    } catch (error) {
      console.error('Stripe init error:', error);
    }
  }

  /**
   * Apple Pay / Google Pay buttons. Stripe only shows them on devices that
   * support them. They use the same order and payment steps as a card.
   */
  function updateWalletButtons(amount) {
    if (!stripe || !(amount >= 50) || !document.getElementById('express-checkout')) return;

    walletAmount = amount;
    if (walletElements) {
      walletElements.update({ amount: amount });
      return;
    }

    try {
      walletElements = stripe.elements({
        mode: 'payment',
        amount: amount,
        currency: config.stripe.currency || 'usd',
        // Card only, so the buttons are just Apple Pay and Google Pay
        allowedPaymentMethodTypes: ['card']
      });

      const walletButtons = walletElements.create('expressCheckout', {
        buttonHeight: 48,
        // Apple Pay and Google Pay only — Stripe would otherwise add Link
        paymentMethods: { link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never' }
      });
      walletButtons.mount('#express-checkout');

      // Hidden until Stripe knows this device has a wallet to show
      walletButtons.on('availablepaymentmethodschange', (event) => {
        document.getElementById('wallet-pay').hidden = !event.paymentMethods;
      });
      walletButtons.on('click', onWalletClick);
      walletButtons.on('confirm', onWalletConfirm);
    } catch (error) {
      console.error('Wallet buttons error:', error);
    }
  }

  function onWalletClick(event) {
    // Stripe waits at most 1 second for an answer, so only quick checks here
    if (document.getElementById('confirm-btn').disabled) {
      event.reject();
      return;
    }
    if (pendingOrder?.paid) {
      showMessage('Your payment already went through. Press "Finish placing order" to finish.', 'error');
      event.reject();
      return;
    }
    if (!validateAgreements()) {
      event.reject();
      return;
    }
    hideMessage();
    event.resolve();
  }

  async function onWalletConfirm(event) {
    let paying = false;
    setConfirmBusy(true);

    try {
      const { error: submitError } = await walletElements.submit();
      if (submitError) throw new Error(submitError.message);

      const orderData = getOrderData();
      await processPayment(orderData, (clientSecret, order) => {
        if (order.totals.total !== walletAmount) {
          // The server priced the order differently (e.g. the menu changed),
          // so show the customer the real total before charging it
          updateWalletButtons(order.totals.total);
          throw new Error(`Your total is now ${formatCurrency(order.totals.total)}. Please press the button again.`);
        }
        paying = true;
        return stripe.confirmPayment({
          elements: walletElements,
          clientSecret: clientSecret,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: { name: orderData.name, email: orderData.email, phone: orderData.phone }
            }
          },
          redirect: 'if_required'
        });
      });
    } catch (error) {
      console.error('Error:', error);
      showMessage(error.message || config.messages?.error, 'error');
      setConfirmBusy(false);
    } finally {
      // Close the wallet sheet if Stripe was never asked to charge it
      if (!paying) event.paymentFailed();
    }
  }

  /**
   * The customer must tick every agreement before paying. Shows the first
   * one missing and returns false.
   */
  function validateAgreements() {
    const terms = document.getElementById('accept-terms');
    if (terms && !terms.checked) {
      showMessage(config.messages?.acceptTerms || 'Please accept the terms and conditions', 'error');
      return false;
    }

    const allergyInfo = document.getElementById('accept-allergy-info');
    if (allergyInfo && !allergyInfo.checked) {
      showMessage('Please confirm you have read the allergen and dietary information', 'error');
      return false;
    }

    const alcoholId = document.getElementById('accept-alcohol-id');
    if (alcoholId && !alcoholId.checked) {
      showMessage(config.messages?.acceptAlcoholId || 'Please accept the alcohol and picture ID policy', 'error');
      return false;
    }

    return true;
  }

  function getOrderData() {
    return {
      name: document.getElementById('takeout-name')?.value.trim(),
      email: document.getElementById('takeout-email')?.value.trim(),
      phone: document.getElementById('takeout-phone')?.value.trim(),
      specialRequests: document.getElementById('takeout-requests')?.value || '',
      orderItems: cart,
      totals: getCartTotals(),
      orderType: 'takeout'
    };
  }

  function setConfirmBusy(busy) {
    const confirmBtn = document.getElementById('confirm-btn');
    if (!confirmBtn.dataset.label) confirmBtn.dataset.label = confirmBtn.innerHTML;
    confirmBtn.disabled = busy;
    if (busy) {
      confirmBtn.innerHTML = config.messages?.processing || 'Processing…';
    } else {
      confirmBtn.innerHTML = pendingOrder?.paid ? 'Finish placing order' : confirmBtn.dataset.label;
    }
  }

  async function confirmOrder() {
    if (!validateAgreements()) return;

    setConfirmBusy(true);

    try {
      const orderData = getOrderData();

      if (config.showPayment && stripe && cardElement) {
        await processPayment(orderData, clientSecret => stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: orderData.name,
              email: orderData.email,
              phone: orderData.phone
            }
          }
        }));
      } else {
        await submitOrder(orderData);
      }

    } catch (error) {
      console.error('Error:', error);
      showMessage(error.message || config.messages?.error, 'error');
      setConfirmBusy(false);
    }
  }

  async function postToOrderApi(action, body) {
    const response = await fetch(`${config.orderApiUrl}?action=${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body)
    });
    return response.json();
  }

  /**
   * Create the order on the server once. Retries (declined card, network
   * error) re-use it, so they never create a second order or charge.
   * Once paid, the order is always re-used even if the cart is edited.
   */
  async function getOrCreateOrder(orderData) {
    const key = JSON.stringify([orderData.name, orderData.email, orderData.phone, orderData.specialRequests, cart]);
    if (pendingOrder && (pendingOrder.paid || pendingOrder.key === key)) {
      return pendingOrder;
    }

    const result = await postToOrderApi('createTakeoutOrder', { order: orderData });
    if (!result.success) {
      throw new Error(result.error || 'Failed to create order');
    }

    pendingOrder = {
      key: key,
      orderId: result.data.orderId,
      totals: result.data.totals || orderData.totals, // server-priced totals
      paid: false,
      paymentId: null
    };
    return pendingOrder;
  }

  /**
   * confirmWithStripe(clientSecret, order) charges the card or wallet and
   * resolves to Stripe's { error, paymentIntent }.
   */
  async function processPayment(orderData, confirmWithStripe) {

    // STEP 1: Create (or re-use) the order — the server prices it from the menu
    const order = await getOrCreateOrder(orderData);

    if (!order.paid) {
      // STEP 2: Create (or re-use) the Stripe payment for this order.
      // The server charges the order's stored total; amount is only sent
      // for older backend versions.
      const intentResult = await postToOrderApi('createPaymentIntent', {
        orderId: order.orderId,
        amount: order.totals.total
      });

      if (!intentResult.success) {
        throw new Error(intentResult.error || 'Failed to create payment');
      }

      if (intentResult.alreadyPaid) {
        order.paid = true;
      } else {
        if (!intentResult.clientSecret) {
          throw new Error('Failed to create payment');
        }

        // STEP 3: Charge the card or wallet with Stripe
        const { error, paymentIntent } = await confirmWithStripe(intentResult.clientSecret, order);

        if (error) {
          if (error.payment_intent?.status !== 'succeeded') throw new Error(error.message);
          order.paid = true; // already paid on an earlier attempt
        } else if (paymentIntent?.status === 'succeeded') {
          order.paid = true;
          order.paymentId = paymentIntent.id;
        } else {
          throw new Error('Your payment was not completed. Please try again.');
        }
      }
    }

    // STEP 4: Confirm order on backend (verifies the payment with Stripe, sends emails)
    const confirmResult = await postToOrderApi('confirmPayment', {
      orderId: order.orderId,
      paymentId: order.paymentId,
      paymentStatus: 'paid'
    });

    if (!confirmResult.success) {
      const callUs = config.restaurantPhone ? `, or call us at ${config.restaurantPhone}` : '';
      throw new Error(
        `Your payment went through, but we couldn't finish placing your order (${confirmResult.error || 'unknown error'}). ` +
        `Please press the button again${callUs} — you won't be charged twice.`
      );
    }

    pendingOrder = null;
    showSuccess(order.orderId, order.totals);
  }

  async function submitOrder(orderData) {
    // Used when payment is disabled
    const result = await postToOrderApi('createTakeoutOrder', { order: orderData });

    if (!result.success) {
      throw new Error(result.error || 'Failed to create order');
    }

    showSuccess(result.data.orderId, result.data.totals || orderData.totals);
  }

  function showSuccess(orderId, totals) {
    closeMobileCart();

    document.getElementById('success-order-id').textContent = orderId || 'PENDING';
    document.getElementById('success-amount').textContent = formatCurrency((totals || getCartTotals()).total);

    const email = document.getElementById('takeout-email')?.value.trim();
    const emailEl = document.getElementById('success-email');
    if (emailEl && email) emailEl.textContent = `We've emailed your receipt to ${email}.`;

    // Show success step
    document.querySelectorAll('.form-step').forEach(el => el.style.display = 'none');
    document.querySelector('[data-form-step="success"]').style.display = 'block';

    // All steps completed
    document.querySelectorAll('.step').forEach(el => el.classList.add('completed'));
    document.querySelectorAll('.step-line').forEach(el => el.classList.add('completed'));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }


  // ============== UTILITIES ==============

  function showMessage(text, type = 'info') {
    const el = document.getElementById('takeout-message');
    if (!el) return;

    el.textContent = text;
    el.className = `takeout-message ${type}`;
    el.style.display = 'block';
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideMessage() {
    const el = document.getElementById('takeout-message');
    if (el) el.style.display = 'none';
  }

  function formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.stripe?.currency?.toUpperCase() || 'USD'
    }).format(cents / 100);
  }

  function formatTag(tag) {
    const map = {
      'vegetarian': 'Vegetarian',
      'vegan': 'Vegan',
      'gluten-free': 'Gluten free'
    };
    return map[tag] || tag;
  }

  function formatSpice(level) {
    const map = {
      'mild': 'Mild',
      'medium': 'Medium',
      'spicy': 'Spicy',
      'extra-spicy': 'Extra spicy'
    };
    return map[level] || (level.charAt(0).toUpperCase() + level.slice(1));
  }

  /**
   * Spice levels offered for an item. The menu sheet's spice_level
   * column lists them, e.g. "mild,medium,spicy". The first is the default.
   */
  function getSpiceOptions(item) {
    const raw = Array.isArray(item?.spice_level) ? item.spice_level : String(item?.spice_level || '').split(',');
    return raw.map(s => String(s).trim().toLowerCase()).filter(Boolean);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }


  // ============== PUBLIC API ==============

  return {
    init,
    goToStep,
    loadMenu,
    addToCart,
    updateCartItem,
    updateCartItemFromCart,
    removeFromCart,
    clearCart,
    clearFilters,
    toggleMobileCart,
    closeMobileCart,
    confirmOrder
  };

})();

// Initialize
document.addEventListener('DOMContentLoaded', TakeoutApp.init);
